import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Bluetooth,
  ExternalLink,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Smartphone,
  Unlink,
  Wifi,
  X,
} from "lucide-react";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { formatDateTime12, getPromise, getPromiseToast, toAbsoluteUrl } from "@/utils/helpers";
import { useAuth } from "@/contexts/use-auth";
import { FACILITY_TIMEZONES } from "@/utils/CommonData";
import type { DeviceV2, PatientV2 } from "../types";
import { PatientV2ExpandableCardActions } from "../MainContent/PatientV2ExpandableCardActions";
import { AddDeviceModal } from "./AddDeviceModal";

function formatDeviceStatus(status: unknown): string {
  if (status == null || status === "") return "—";
  const spaced = String(status).replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatSimpleDate(value: unknown): string {
  if (value == null || value === "") return "—";
  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return str;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

function formatTimeAgo(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
}

function getStatusBadgeClass(status: string): string {
  const key = status.toLowerCase();
  if (key.includes("deliver") || key === "active") {
    return "border-green-200 bg-green-50 text-green-700";
  }
  if (key.includes("transit") || key.includes("ship")) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (key.includes("pending") || key.includes("progress")) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-muted-foreground/20 bg-muted text-muted-foreground";
}

function isBluetoothDevice(
  device: DeviceV2 & Record<string, unknown>,
): boolean {
  const connectionType = String(device?.connectionType ?? "").toLowerCase();
  return connectionType.includes("bluetooth") || connectionType === "ble";
}

type EnrichedDevice = {
  raw: DeviceV2;
  deviceName: string;
  deviceType: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  imei: string;
  commonTimeZone: string;
  status: string;
  nextStep: string;
  trackingNumber: string;
  trackingUrl: string;
  expectedDateOfDelivery: string;
  assignedAt: string;
  lastReadingAt: string;
  lastSyncAt: string;
  isBluetooth: boolean;
};

export function PatientDevicesList({
  patient,
  devices,
  resolvedTimezone = "",
  onRefresh,
}: {
  patient: PatientV2 | null;
  devices: DeviceV2[];
  resolvedTimezone?: string;
  onRefresh?: () => Promise<void>;
}) {
  const patientId = String(patient?._id ?? "");
  const [rows, setRows] = useState<DeviceV2[]>(devices || []);
  const [searchInput, setSearchInput] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceV2 | null>(null);
  const { user, hasModuleAdminAccess } = useAuth();
  const isProviderOrPractitionerManager =
    user?.role === "manager" &&
    (user?.managerScope === "provider" ||
      user?.managerScope === "practitioner");
  const isAdmin = hasModuleAdminAccess("canAccessPatients");
  const canDisassociate = isAdmin && !isProviderOrPractitionerManager;
  const isOutpatient = patient?.typeOfPatient === "outpatient";

  useEffect(() => {
    setRows(devices || []);
  }, [devices]);

  const handleSyncDevice = useCallback(async () => {
    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    try {
      setSyncLoading(true);
      await AxiosInstance().post("/devices/impilo/sync-patient", {
        patientId,
      });
      toast.success("Device synced successfully.");
      await onRefresh?.();
    } catch (error: unknown) {
      const message =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        "Failed to sync device";
      toast.error(message);
    } finally {
      setSyncLoading(false);
    }
  }, [patientId, onRefresh]);

  const commonTimeZone = useMemo(
    () =>
      FACILITY_TIMEZONES.find((item) => item.value === resolvedTimezone)
        ?.label || "",
    [resolvedTimezone],
  );

  const enrichedDevices = useMemo<EnrichedDevice[]>(
    () =>
      [...(rows || [])]
        .sort((a, b) => {
          const aTime = (a as DeviceV2 & { assignedAt?: string })?.assignedAt
            ? new Date(
                (a as DeviceV2 & { assignedAt?: string }).assignedAt!,
              ).getTime()
            : 0;
          const bTime = (b as DeviceV2 & { assignedAt?: string })?.assignedAt
            ? new Date(
                (b as DeviceV2 & { assignedAt?: string }).assignedAt!,
              ).getTime()
            : 0;
          return bTime - aTime;
        })
        .map((device) => {
          const d = device as DeviceV2 & Record<string, unknown>;
          return {
            raw: device,
            deviceName: String(d?.deviceName ?? d?.name ?? "—"),
            deviceType: String(d?.deviceType ?? d?.type ?? "—").replace(
              /_/g,
              " ",
            ),
            manufacturer: String(d?.manufacturer ?? "—"),
            model: String(d?.model ?? "—"),
            serialNumber: String(d?.serialNumber ?? "—"),
            imei: String(d?.imei ?? "—"),
            commonTimeZone: commonTimeZone || "—",
            status: formatDeviceStatus(d?.status),
            nextStep: formatDeviceStatus(d?.nextStep),
            trackingNumber: String(d?.trackingNumber ?? "—"),
            trackingUrl: String(d?.trackingUrl ?? ""),
            expectedDateOfDelivery: formatSimpleDate(d?.expectedDateOfDelivery),
            assignedAt: d?.assignedAt
              ? formatDateTime12(String(d.assignedAt))
              : "—",
            lastReadingAt: formatTimeAgo(d?.lastReadingAt),
            lastSyncAt: formatTimeAgo(d?.lastSyncAt),
            isBluetooth: isBluetoothDevice(d),
          };
        }),
    [rows, commonTimeZone],
  );

  const searchQuery = searchInput.trim().toLowerCase();
  const filteredDevices = useMemo(() => {
    if (!searchQuery) return enrichedDevices;
    return enrichedDevices.filter((device) =>
      [
        device.deviceName,
        device.deviceType,
        device.serialNumber,
        device.imei,
        device.trackingNumber,
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchQuery),
    );
  }, [enrichedDevices, searchQuery]);

  const cellularDevices = useMemo(
    () => filteredDevices.filter((device) => !device.isBluetooth),
    [filteredDevices],
  );
  const bluetoothDevices = useMemo(
    () => filteredDevices.filter((device) => device.isBluetooth),
    [filteredDevices],
  );

  const handleDisassociate = useCallback(
    async (device: DeviceV2) => {
      if (!device._id) {
        toast.error("Device ID is missing");
        return;
      }

      const { pr, rejectFn, resolveFn } = getPromise();
      getPromiseToast(pr);

      try {
        setBtnLoading(true);
        if(device?.connectionType === "cellular") {
        await AxiosInstance().post("/devices/disassociate", {
          deviceId: device._id,
        });
        }
        // "Bluetooth devices cannot be disassociated through Impilo. Use DELETE /api/devices/bluetooth/:deviceId"
        if(device?.connectionType === "bluetooth") {
          await AxiosInstance().delete(`/devices/bluetooth/${device._id}`);
        }
        resolveFn("Device disassociated successfully");
        await onRefresh?.();
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to disassociate device";
        rejectFn(error);
        toast.error(message);
      } finally {
        setBtnLoading(false);
      }
    },
    [onRefresh],
  );

  const handleEdit = useCallback((device: DeviceV2) => {
    setEditingDevice(device);
    setIsDeviceModalOpen(true);
  }, []);

  const cardActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: <Pencil className="h-4 w-4" />,
        onClick: (row: unknown) => handleEdit(row as DeviceV2),
        isDisabled: () => btnLoading,
      },
      ...(canDisassociate
        ? [
            {
              label: "Disassociate",
              icon: <Unlink className="h-4 w-4" />,
              onClick: (row: unknown) => handleDisassociate(row as DeviceV2),
              isDisabled: () => btnLoading,
            },
          ]
        : []),
    ],
    [canDisassociate, handleDisassociate, handleEdit, btnLoading],
  );

  const renderDeviceCard = (device: EnrichedDevice) => {
    const suffix = device.isBluetooth ? "(BLE)" : "(LTE)";
    const subtitleParts =
      device.imei !== "—"
        ? [`IMEI: ${device.imei}`, `Serial: ${device.serialNumber}`]
        : [
            `Serial: ${device.serialNumber}`,
            `Tracking: ${device.trackingNumber}`,
          ];

    return (
      <div
        key={device.raw._id ?? device.deviceName}
        className="rounded-lg border border-border bg-card p-4 shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
              {device.isBluetooth ? (
                <Bluetooth className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Smartphone className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="truncate text-[14px] font-medium leading-snug text-[#374151]">
                  {device.deviceName} {suffix}
                </p>
                <Badge className={getStatusBadgeClass(device.status)}>
                  {device.status}
                </Badge>
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {subtitleParts
                  .filter((part) => !part.endsWith(": —"))
                  .join(" · ")}
              </p>
            </div>
          </div>
          <PatientV2ExpandableCardActions
            actions={cardActions}
            row={device.raw}
            disabled={btnLoading}
          />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Next Step
            </p>
            {device.nextStep !== "—" ? (
              <Badge className="mt-0.5 border-amber-200 bg-amber-50 text-amber-700">
                {device.nextStep}
              </Badge>
            ) : (
              <p className="mt-0.5 text-[14px] text-foreground">—</p>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Expected Date of Delivery
            </p>
            <p className="mt-0.5 truncate text-[14px] text-foreground">
              {device.expectedDateOfDelivery}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Serial Number
            </p>
            <p className="mt-0.5 truncate text-[14px] text-foreground">
              {device.serialNumber}
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              IMEI Number
            </p>
            <p className="mt-0.5 truncate text-[14px] text-foreground">
              {device.imei}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Tracking ID &amp; URL
            </p>
            <p className="mt-0.5 flex items-center gap-1 truncate text-[14px] text-foreground">
              {device.trackingNumber}
              {device.trackingUrl ? (
                <a
                  href={toAbsoluteUrl(device.trackingUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-blue-600 hover:text-blue-700"
                  aria-label="Open tracking URL"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : null}
            </p>
          </div>
        </div>

        <div className="mt-4 border-t pt-3 text-xs text-muted-foreground">
          {device.isBluetooth
            ? `LAST SYNC: ${device.lastSyncAt}`
            : `LAST READING: ${device.lastReadingAt}`}
        </div>
      </div>
    );
  };

  return (
    <div className="teachers space-y-5">
      <div className="flex w-full min-w-0 flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search devices..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute right-3 top-3 h-4 w-4 text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 md:ml-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!patientId}
            onClick={() => {
              setEditingDevice(null);
              setIsDeviceModalOpen(true);
            }}
            className="shrink-0"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Device
          </Button>
          {isOutpatient ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={syncLoading || !patientId}
              onClick={handleSyncDevice}
              className="shrink-0"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${syncLoading ? "animate-spin" : ""}`}
              />
              {syncLoading ? "Syncing…" : "Sync Device"}
            </Button>
          ) : null}
        </div>
      </div>

      {filteredDevices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/50 bg-transparent p-4 text-sm text-muted-foreground">
          No devices found.
        </div>
      ) : (
        <>
          {cellularDevices.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Wifi className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  Cellular Devices
                </p>
                <Badge variant="outline">{cellularDevices.length}</Badge>
                <span className="text-xs text-muted-foreground">
                  Direct 4G/LTE cellular connected
                </span>
              </div>
              <div className="space-y-3">
                {cellularDevices.map(renderDeviceCard)}
              </div>
            </div>
          )}

          {bluetoothDevices.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Bluetooth className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  Bluetooth Devices
                </p>
                <Badge variant="outline">{bluetoothDevices.length}</Badge>
                <span className="text-xs text-muted-foreground">
                  BLE paired devices synced via mobile/hub app
                </span>
              </div>
              <div className="space-y-3">
                {bluetoothDevices.map(renderDeviceCard)}
              </div>
            </div>
          )}
        </>
      )}

      <AddDeviceModal
        open={isDeviceModalOpen}
        onOpenChange={(nextOpen) => {
          setIsDeviceModalOpen(nextOpen);
          if (!nextOpen) {
            setEditingDevice(null);
          }
        }}
        patient={patient}
        patientId={patientId}
        device={editingDevice}
        onDeviceAdded={onRefresh}
      />
    </div>
  );
}
